import * as React from 'react';
import {
  List,
  Datagrid,
  DateField,
  TextField,
  BooleanField,
  ListProps,
  TextInput,
  Show,
  SimpleShowLayout,
  ReferenceField,
  ReferenceManyField,
  useRecordContext,
  useListContext,
  FieldProps,
} from 'react-admin';
import { Box, Typography, Tooltip } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

import { Session, Event } from '../domain';
import { UuidField } from '../components/fields';
import { DefaultPagination } from '../components/pagination';

export const SessionsIcon = VpnKeyIcon;

const shortenUuid = (value: string) => value.slice(0, 8);

// Clickable text field that sets a filter when clicked
const ClickableTextField: React.FC<FieldProps> = ({ source = '' }) => {
  const record = useRecordContext<Session>();
  const { filterValues, setFilters } = useListContext();
  
  if (!record) return null;
  
  const value = record[source as keyof Session];
  if (!value) return <TextField source={source} />;
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newFilters = { ...filterValues, [source]: value };
    setFilters(newFilters, null);
  };
  
  return (
    <span
      onClick={handleClick}
      style={{
        cursor: 'pointer',
        textDecoration: 'underline',
        color: '#1976d2',
      }}
    >
      {String(value)}
    </span>
  );
};

// Clickable UUID field that sets a filter when clicked
const ClickableUuidField: React.FC<FieldProps & { label?: string }> = ({ 
  source = '', 
  label 
}) => {
  const record = useRecordContext<Session>();
  const { filterValues, setFilters } = useListContext();
  
  if (!record) return null;
  
  const value = record[source as keyof Session];
  if (!value) return <UuidField source={source} label={label} />;
  
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newFilters = { ...filterValues, [source]: value };
    setFilters(newFilters, null);
  };
  
  return (
    <Tooltip title={String(value)}>
      <Typography
        variant="body2"
        onClick={handleClick}
        style={{
          cursor: 'pointer',
          textDecoration: 'underline',
          color: '#1976d2',
          display: 'inline-block',
        }}
      >
        {shortenUuid(String(value))}
      </Typography>
    </Tooltip>
  );
};

const sessionFilters = [
  <TextInput
    key="device_id"
    label="Device ID"
    source="device_id"
    parse={(value) => (value === '' ? undefined : value)}
  />,
  <TextInput
    key="participant_id"
    label="Participant ID"
    source="participant_id"
    parse={(value) => (value === '' ? undefined : value)}
  />,
  <TextInput
    key="show_id"
    label="Show ID"
    source="show_id"
    parse={(value) => (value === '' ? undefined : value)}
  />,
  <TextInput
    key="app_version"
    label="App Version"
    source="app_version"
    parse={(value) => (value === '' ? undefined : value)}
  />,
];

export const SessionList: React.FC<ListProps> = (props) => (
  <List
    {...props}
    perPage={25}
    pagination={<DefaultPagination />}
    exporter={false}
    sort={{ field: 'created_at', order: 'DESC' }}
    filters={sessionFilters}
  >
    <Datagrid expand={<SessionExpandDetails />} rowClick="show">
      <UuidField source="id" />
      <ClickableTextField source="device_id" />
      <ReferenceField source="participant_id" reference="participant" link="show">
        <TextField source="name" />
      </ReferenceField>
      <ClickableUuidField source="participant_id" label="Participant ID" />
      <TextField source="show_id" />
      <TextField source="lang" />
      <TextField source="app_version" />
      <BooleanField source="is_persistent" />
      <DateField source="created_at" showTime={true} locales={['en-GB']} />
    </Datagrid>
  </List>
);

const SessionExpandDetails: React.FC = () => {
  const record = useRecordContext<Session>();
  if (!record) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>Session Details</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="caption" color="text.secondary">Token</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {record.token}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Device ID</Typography>
          <Typography variant="body2">{record.device_id}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">App Version</Typography>
          <Typography variant="body2">{record.app_version}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">Is Persistent</Typography>
          <Typography variant="body2">{record.is_persistent ? 'Yes' : 'No'}</Typography>
        </Box>
      </Box>
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Events for this Session</Typography>
        <ReferenceManyField
          reference="event"
          target="session_id"
          record={record}
        >
          <SessionEventsList />
        </ReferenceManyField>
      </Box>
    </Box>
  );
};

const SessionEventsList: React.FC = () => {
  const { data, isLoading } = useListContext<Event>();
  const events = data ? Object.values(data) : [];

  if (isLoading) {
    return <Typography>Loading events...</Typography>;
  }

  if (events.length === 0) {
    return <Typography color="text.secondary">No events found for this session.</Typography>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {events.length} event{events.length !== 1 ? 's' : ''} found
      </Typography>
      <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
        {events
          .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
          .map((event) => (
            <Box
              key={event.id}
              sx={{
                p: 1.5,
                mb: 1,
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                backgroundColor: '#fafafa',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  {event.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(event.occurred_at).toLocaleString('en-GB')}
                </Typography>
              </Box>
              {event.participant_id && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Participant: {event.participant_id.slice(0, 8)}...
                </Typography>
              )}
            </Box>
          ))}
      </Box>
    </Box>
  );
};

export const SessionShow: React.FC = () => {
  return (
    <Show>
      <SimpleShowLayout>
        <UuidField source="id" />
        <TextField source="device_id" />
        <ReferenceField source="participant_id" reference="participant" link="show">
          <TextField source="name" />
        </ReferenceField>
        <UuidField source="participant_id" label="Participant ID" />
        <TextField source="show_id" />
        <TextField source="lang" />
        <TextField source="app_version" />
        <BooleanField source="is_persistent" />
        <TextField source="token" />
        <DateField source="created_at" showTime={true} locales={['en-GB']} />
        
        <Box sx={{ mt: 3 }}>
          <ReferenceManyField
            reference="event"
            target="session_id"
          >
            <SessionEventsList />
          </ReferenceManyField>
        </Box>
      </SimpleShowLayout>
    </Show>
  );
};

